"use client"

import type React from "react"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar"
import { Card, CardContent } from "../../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Upload, User, UserPlus, Trash2, Phone, Users } from "lucide-react"
import type { Seller } from "../../projects"
import { registerSeller } from "../../API"

type SellerManagementModalProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    sellers: Seller[]
    onAddSeller: (seller: Omit<Seller, "id">) => void
    onRemoveSeller: (id: string, seller_id: number) => void
}

export function SellerManagementModal({
    open,
    onOpenChange,
    sellers,
    onAddSeller,
    onRemoveSeller,
}: SellerManagementModalProps) {
    const [name, setName] = useState("")
    const [phone, setPhone] = useState("")
    const [photo, setPhoto] = useState<string | null>(null)

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, "")
        if (value.length > 11) value = value.slice(0, 11)

        if (value.length > 6) {
            value = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`
        } else if (value.length > 2) {
            value = `(${value.slice(0, 2)}) ${value.slice(2)}`
        } else if (value.length > 0) {
            value = `(${value}`
        }

        setPhone(value)
    }

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setPhoto(reader.result as string)
            }
            reader.readAsDataURL(file)
        }
    }

    async function dataURLtoFile(dataUrl: string, filename: string): Promise<File> {
        return new Promise((resolve, reject) => {
            try {
                const arr = dataUrl.split(',');
                const mime = arr[0].match(/:(.*?);/)?.[1] || '';
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);

                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }

                resolve(new File([u8arr], filename, { type: mime }));
            } catch (err) {
                reject(err);
            }
        });
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim() || !phone.trim()) return

        onAddSeller({
            name: name.trim(),
            phone: phone.trim(),
            photo,
            seller_id: Math.random()
        })

        if (photo) {
            const photo2 = await dataURLtoFile(photo, "image.png");

            registerSeller({
                name: name.trim(),
                phone: phone.trim(),
                photo2,
            });
        }

        setName("")
        setPhone("")
        setPhoto(null)
    }

    (window as { [key: string]: any }).handleAddSellers = async (data: any) => {
        onAddSeller({
            name: data.name,
            phone: data.phone,
            photo: "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/" + data.thumb,
            seller_id: data.id
        })

        setName("")
        setPhone("")
        setPhoto(null)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                        <Users className="h-6 w-6" />
                        Gestão de Vendedores
                    </DialogTitle>
                </DialogHeader>

                <Tabs defaultValue="register" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="register" className="gap-2">
                            <UserPlus className="h-4 w-4" />
                            Registrar
                        </TabsTrigger>
                        <TabsTrigger value="list" className="gap-2">
                            <Users className="h-4 w-4" />
                            Vendedores ({sellers.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="register" className="space-y-6 mt-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex flex-col items-center gap-4">
                                <Avatar className="h-24 w-24">
                                    <AvatarImage src={photo || undefined} />
                                    <AvatarFallback className="bg-muted">
                                        <User className="h-12 w-12 text-muted-foreground" />
                                    </AvatarFallback>
                                </Avatar>
                                <Label htmlFor="photo" className="cursor-pointer">
                                    <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                                        <Upload className="h-4 w-4" />
                                        <span className="text-sm font-medium">Carregar Foto</span>
                                    </div>
                                    <Input id="photo" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                                </Label>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name">Nome Completo</Label>
                                <Input
                                    id="name"
                                    placeholder="Digite o nome do vendedor"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Número de Telefone</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    required
                                />
                            </div>

                            <Button type="submit" className="w-full gap-2">
                                <UserPlus className="h-4 w-4" />
                                Registrar Vendedor
                            </Button>
                        </form>
                    </TabsContent>

                    <TabsContent value="list" className="mt-6">
                        {sellers.length > 0 ? (
                            <div className="space-y-4">
                                {sellers.map((seller) => (
                                    <Card key={seller.id}>
                                        <CardContent className="flex items-center justify-between p-4">
                                            <div className="flex items-center gap-3">
                                                <Avatar className="h-12 w-12">
                                                    <AvatarImage src={seller.photo || undefined} />
                                                    <AvatarFallback className="bg-muted">
                                                        <User className="h-6 w-6 text-muted-foreground" />
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="space-y-1">
                                                    <h3 className="font-semibold text-foreground">{seller.name}</h3>
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                                                        <Phone className="h-3 w-3" />
                                                        {seller.phone}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="destructive"
                                                size="icon"
                                                onClick={() => onRemoveSeller(seller.id, seller.seller_id)}
                                                className="h-9 w-9"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="border-dashed">
                                <CardContent className="flex flex-col items-center justify-center py-12">
                                    <User className="h-12 w-12 text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground text-center">
                                        Nenhum vendedor cadastrado ainda.
                                        <br />
                                        Vá para a aba Registrar para adicionar vendedores.
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}
